import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface ConsentStatus {
  accepted: boolean;
  currentVersion: number;
}

@Injectable({ providedIn: 'root' })
export class AiConsentService {
  private readonly http = inject(HttpClient);

  private readonly statusUrl = `${environment.apiBaseUrl}${environment.endpoint.consent.aiReportsStatus}`;
  private readonly acceptUrl = `${environment.apiBaseUrl}${environment.endpoint.consent.aiReportsAccept}`;

  private readonly _status = signal<ConsentStatus | null>(null);
  private readonly _fetchFailed = signal(false);
  private _fetching = false;

  /** null means not yet loaded — callers must not treat null as either accepted or rejected. */
  readonly status = this._status.asReadonly();
  readonly fetchFailed = this._fetchFailed.asReadonly();

  fetchStatus(): void {
    if (this._status() !== null) return;
    if (this._fetching) return;
    this._fetching = true;
    this._fetchFailed.set(false);
    this.http.get<ConsentStatus>(this.statusUrl).pipe(timeout(10_000)).subscribe({
      next: (status) => { this._fetching = false; this._status.set(status); },
      error: (err) => {
        this._fetching = false;
        this._fetchFailed.set(true);
        console.error('[AiConsent] Failed to fetch consent status:', err);
      },
    });
  }

  /** Caller subscribes and handles its own loading/error UI state (same pattern as payWithVipps()). */
  accept(): Observable<void> {
    return this.http.post<void>(this.acceptUrl, {}).pipe(
      timeout(30_000),
      tap(() => {
        const current = this._status();
        this._status.set({ accepted: true, currentVersion: current?.currentVersion ?? 0 });
        this._fetchFailed.set(false);
      }),
    );
  }
}
